import {
  BlockChange,
  ColorBlock,
  isBlockWithVariantOverrides,
  isColorBlock,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { hexToRgb, rgbToHsl } from '~/lib/color'
import { Input } from '~/sotano-kit/primitives/input'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'
import * as React from 'react'
import { invariant } from '@epic-web/invariant'

type ColorInputDiffProps = {
  condition: 'added' | 'removed'
  blockChange: BlockChange
}

export const ColorInputDiff = ({
  condition,
  blockChange,
}: ColorInputDiffProps) => {
  const value = React.useMemo(
    () =>
      (condition === 'removed' || blockChange.status === 'deleted'
        ? blockChange?.conflicting?.value ?? blockChange?.previous?.value
        : blockChange?.current?.value) as string | null,
    [blockChange, condition]
  )

  return (
    <>
      <DiffItem
        status={blockChange.status}
        condition={condition}
        value={value}
      />
      <OverridesDiff condition={condition} blockChange={blockChange} />
    </>
  )
}

const DiffItem = ({
  status,
  condition,
  value,
  className,
}: {
  status: BlockChange['status']
  condition: 'added' | 'removed'
  value: string | null
  className?: string
}) => {
  const rgb = hexToRgb(value ?? '#000')
  const HSL = rgb ? rgbToHsl(...rgb) : []
  const [H, S, L] = HSL
  const hsl = `${H}deg ${S}% ${L}%`

  return (
    <div
      className={clsx(
        'flex flex-col border border-dashed border-transparent rounded-md',
        className,
        {
          '!border-green-elevated': status === 'created',
          '!border-red-elevated': status === 'deleted',
          '!border-yellow': status === 'modified' && condition === 'added',
          '!bg-blue-dark': status === 'reordered',
          'opacity-30': condition === 'added' && status === 'deleted',
        }
      )}
    >
      <Input
        readOnly
        value={value ?? '#000'}
        type="color"
        className="z-10 rounded-b-none pointer-events-none border-b-transparent outline-offset-3"
      />
      <div className="grid items-center grid-cols-[repeat(3,minmax(auto,1fr))] whitespace-nowrap px-2 py-1 text-xs border-b cursor-text border-x border-dark-border-solid rounded-b-md bg-dark-shade text-dark-muted">
        <span className="pr-2">{value}</span>
        <span className="mx-auto max-w-max px-2">
          rgb({(rgb ?? []).join(', ')})
        </span>
        <span className="relative ml-auto max-w-max pl-2">hsl({hsl})</span>
      </div>
    </div>
  )
}

const OverridesDiff = ({ condition, blockChange }: ColorInputDiffProps) => {
  const overrides = React.useMemo(() => {
    invariant(
      !blockChange?.current || isColorBlock(blockChange.current),
      'Current value should be color'
    )
    if (condition === 'removed' || blockChange.status === 'deleted') {
      if (
        !blockChange.previous ||
        !isBlockWithVariantOverrides(blockChange.previous)
      ) {
        return []
      }
      return Object.entries(blockChange?.previous?.variantOverrides ?? {}).map(
        ([key, { value }]) => ({
          overrideKey: key,
          status:
            (blockChange.current as ColorBlock)?.variantOverrides?.[key]
              ?.value !== value
              ? 'modified'
              : blockChange.status,
          condition,
          value,
        })
      )
    }

    return Object.entries(blockChange?.current?.variantOverrides ?? {}).map(
      ([key, { value }]) => ({
        overrideKey: key,
        status:
          (blockChange.previous as ColorBlock)?.variantOverrides?.[key]
            ?.value !== value
            ? 'modified'
            : blockChange.status,
        condition,
        value,
      })
    )
  }, [blockChange, condition])

  if (!overrides.length) return null
  return (
    <OverridesDiffWrapper<(typeof overrides)[number]> overrides={overrides}>
      {DiffItem}
    </OverridesDiffWrapper>
  )
}
